import type { FormFieldMapping } from '../types/fieldMapping';
import { resolveSyncFormatter, type SyncFormatter } from './resolveFieldDisplayValue';

interface ReadonlyFieldState {
  formatter?: SyncFormatter;
}

function conditionFieldKey(condition: unknown): string | undefined {
  if (condition == null || typeof condition !== 'object') return undefined;
  const field = (condition as Record<string, unknown>).field;
  if (typeof field !== 'string') return undefined;
  return field.replace(/^#\/properties\//, '').replace(/\/properties\//g, '.');
}

/**
 * Resolves the active sync formatter for read-only display.
 *
 * Conditional formatters (inputFormatterWhen) depend on derived flags like
 * `isOtherCountryPhone` that are set via optionProp effects. In view mode,
 * async selects do not mount — option meta may be missing until seeded.
 *
 * When option metadata is available, prefer it over stale form defaults.
 */
export function resolveReadonlyFormatter(
  field: FormFieldMapping,
  fieldState: ReadonlyFieldState,
  getOptionProp: (fieldName: string, prop: string) => unknown,
): SyncFormatter | undefined {
  const formatterWhen = field.when?.formatter;
  if (formatterWhen) {
    const condField = conditionFieldKey(formatterWhen.condition);
    if (condField === 'isOtherCountryPhone') {
      const isOtherCountry = getOptionProp('workNumberCountryCodeId', 'isOtherCountry');
      if (isOtherCountry === true) {
        return formatterWhen.trueFormatter ?? fieldState.formatter;
      }
      if (isOtherCountry === false) {
        return formatterWhen.falseFormatter ?? fieldState.formatter;
      }
      // Country code options not loaded yet — skip US formatting rather than misformat.
      return undefined;
    }

    return fieldState.formatter;
  }

  if (fieldState.formatter) return fieldState.formatter;
  return resolveSyncFormatter(field.def.formatter as FormFieldMapping['def']['formatter']);
}
